import { createRouteFor } from '@components-common/routes';
import { indexBy, map } from "ramda";
import { componentsPath } from "../shared";

const componentsBase = "components_db_components";

export type BuildingComponentType = "window" | "floor" | "wall" | "roof";

// Define a type for the dynamically created routes
type ComponentRoute<Type extends BuildingComponentType> =
  | { id: `${typeof componentsBase}${Type}s`, to: `${typeof componentsPath}${Type}s` }
  | { id: `${typeof componentsBase}${Type}s_parameters`, to: `${typeof componentsPath}${Type}s/parameters` }
  | { id: `${typeof componentsBase}${Type}s_parameters_new`, to: `${typeof componentsPath}${Type}s/parameters/new` }
  | { id: `${typeof componentsBase}${Type}s_parameters_edit`, to: `${typeof componentsPath}${Type}s/parameters/:parameterId` }
  | { id: `${typeof componentsBase}${Type}s_new`, to: `${typeof componentsPath}${Type}s/new` }
  | { id: `${typeof componentsBase}${Type}s_${Type}Id`, to: `${typeof componentsPath}${Type}s` }
  | { id: `${typeof componentsBase}${Type}s_${Type}Id_parameters`, to: `${typeof componentsPath}${Type}s` }
  | { id: `${typeof componentsBase}${Type}s_${Type}Id_parameters_new`, to: `${typeof componentsPath}${Type}s` }
  | { id: `${typeof componentsBase}${Type}s_${Type}Id_parameters_edit`, to: `${typeof componentsPath}${Type}s` };

type CreateComponentRoute = <Type extends BuildingComponentType>(
  type: Type
) => ComponentRoute<Type>[];

export const createComponentRoute: CreateComponentRoute = <Type extends BuildingComponentType>(
  type: Type
): ComponentRoute<Type>[] => {
  const baseRoute = `components_db_components${type}s` as const;
  const parameterRoute = `${baseRoute}_parameters` as const;

  return [
    {
      id: baseRoute,
      to: `${componentsPath}${type}s`
    },
    {
      id: parameterRoute,
      to: `${componentsPath}${type}s/parameters`
    },
    {
      id: `${parameterRoute}_new`,
      to: `${componentsPath}${type}s/parameters/new`
    },
    {
      id: `${parameterRoute}_edit`,
      to: `${componentsPath}${type}s/parameters/:parameterId`
    },
    {
      id: `${baseRoute}_new`,
      to: `${componentsPath}${type}s/new`
    },
    {
      id: `${baseRoute}_${type}Id`,
      to: `${componentsPath}${type}s`
    },
    {
      id: `${baseRoute}_${type}Id_parameters`,
      to: `${componentsPath}${type}s`
    },
    {
      id: `${baseRoute}_${type}Id_parameters_new`,
      to: `${componentsPath}${type}s`
    },
    {
      id: `${baseRoute}_${type}Id_parameters_edit`,
      to: `${componentsPath}${type}s`
    }
  ];
};

export const createRouteType = <Type extends BuildingComponentType>(type: Type) =>
  createComponentRoute(type).map(route => ({
    ...route,
    path: `/components/${type}s`,
    label: `${type}s`
  }));

export const floorRoutes = createRouteType("floor");
export type FloorRouteIds = typeof floorRoutes[number]["id"];

export const windowRoutes = createRouteType("window");
export type WindowRouteIds = typeof windowRoutes[number]["id"];

export const wallRoutes = createRouteType("wall");
export type WallRouteIds = typeof wallRoutes[number]["id"];

export const roofRoutes = createRouteType("roof");
export type RoofRouteIds = typeof roofRoutes[number]["id"];

const getRouteId = <T extends BuildingComponentType>(route:ComponentRoute<T>) => route.id;
export const componentDbRoutes = {
  dashboard: {
    id: "components_db_dashboard",
    to: "/components-db",
    label: "Components database"
  },
  floor: indexBy(getRouteId, floorRoutes),
  window: indexBy(getRouteId,windowRoutes),
  wall: indexBy(getRouteId,wallRoutes),
  roof: indexBy(getRouteId,roofRoutes)
};
